import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { BRAND } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    inquiry_type: "general",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || form.message.length < 5) {
      toast.error("Please fill name, email and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received — asante sana. We'll reply soon.");
      setForm({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
        inquiry_type: "general",
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 lg:py-28 bg-brand-sand/40"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-forest font-semibold">
              Get in touch
            </div>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-ink leading-[1.05]">
              Karibu —{" "}
              <span className="italic text-brand-forest">say hello.</span>
            </h2>
            <p className="mt-5 text-brand-muted text-lg leading-relaxed max-w-md">
              Need support? Want to partner? Have a story to share? We read
              every message.
            </p>

            <div className="mt-8 space-y-4">
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="contact-email-link"
                className="flex items-center gap-4 rounded-2xl border border-brand-border bg-white p-4 hover-lift"
              >
                <div className="h-11 w-11 rounded-xl bg-brand-forest/10 text-brand-forest flex items-center justify-center">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] text-brand-muted">
                    Email
                  </div>
                  <div className="text-brand-ink font-medium">
                    {BRAND.email}
                  </div>
                </div>
              </a>
              <a
                href={`tel:${BRAND.phone.replace(/\s+/g, "")}`}
                data-testid="contact-phone-link"
                className="flex items-center gap-4 rounded-2xl border border-brand-border bg-white p-4 hover-lift"
              >
                <div className="h-11 w-11 rounded-xl bg-brand-forest/10 text-brand-forest flex items-center justify-center">
                  <Phone className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] text-brand-muted">
                    Phone
                  </div>
                  <div className="text-brand-ink font-medium">{BRAND.phone}</div>
                </div>
              </a>
              <div className="flex items-center gap-4 rounded-2xl border border-brand-border bg-white p-4">
                <div className="h-11 w-11 rounded-xl bg-brand-forest/10 text-brand-forest flex items-center justify-center">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.16em] text-brand-muted">
                    Office
                  </div>
                  <div className="text-brand-ink font-medium">
                    {BRAND.address}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <motion.form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 rounded-[24px] bg-white ring-1 ring-brand-border p-7 lg:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-brand-muted font-semibold">
                  Full name
                </label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  data-testid="contact-name-input"
                  required
                  className="mt-2 w-full rounded-xl border border-brand-border bg-brand-cream/60 px-4 py-3 outline-none focus:ring-2 focus:ring-brand-forest/40 focus:border-brand-forest text-brand-ink"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-brand-muted font-semibold">
                  Email
                </label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  data-testid="contact-email-input"
                  required
                  className="mt-2 w-full rounded-xl border border-brand-border bg-brand-cream/60 px-4 py-3 outline-none focus:ring-2 focus:ring-brand-forest/40 focus:border-brand-forest text-brand-ink"
                  placeholder="you@email.com"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-brand-muted font-semibold">
                  Phone (optional)
                </label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  data-testid="contact-phone-input"
                  className="mt-2 w-full rounded-xl border border-brand-border bg-brand-cream/60 px-4 py-3 outline-none focus:ring-2 focus:ring-brand-forest/40 focus:border-brand-forest text-brand-ink"
                  placeholder="+255 ..."
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-brand-muted font-semibold">
                  Topic
                </label>
                <select
                  name="inquiry_type"
                  value={form.inquiry_type}
                  onChange={handleChange}
                  data-testid="contact-topic-select"
                  className="mt-2 w-full rounded-xl border border-brand-border bg-brand-cream/60 px-4 py-3 outline-none focus:ring-2 focus:ring-brand-forest/40 focus:border-brand-forest text-brand-ink"
                >
                  <option value="general">General inquiry</option>
                  <option value="support">I need support</option>
                  <option value="partnership">Partnership</option>
                  <option value="media">Media / Press</option>
                  <option value="donation">Donation question</option>
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="text-xs uppercase tracking-[0.16em] text-brand-muted font-semibold">
                Subject (optional)
              </label>
              <input
                name="subject"
                value={form.subject}
                onChange={handleChange}
                data-testid="contact-subject-input"
                className="mt-2 w-full rounded-xl border border-brand-border bg-brand-cream/60 px-4 py-3 outline-none focus:ring-2 focus:ring-brand-forest/40 focus:border-brand-forest text-brand-ink"
                placeholder="What's this about?"
              />
            </div>

            <div className="mt-4">
              <label className="text-xs uppercase tracking-[0.16em] text-brand-muted font-semibold">
                Message
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                data-testid="contact-message-input"
                required
                rows={5}
                className="mt-2 w-full rounded-xl border border-brand-border bg-brand-cream/60 px-4 py-3 outline-none focus:ring-2 focus:ring-brand-forest/40 focus:border-brand-forest text-brand-ink"
                placeholder="Tell us how we can help."
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-button"
              className="btn-primary mt-6 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Sending..." : "Send message"}
              <Send className="h-4 w-4" />
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
